import React, { useId } from "react";
import "./VerifiedCelebration.css";

// The moment Instagram vouches for someone (founder pick A, 2026-11-02): our
// green rosette pops in, the tick draws itself, a gold ribbon unrolls across
// it, then stars and a heart. It plays once, at the top of "Instagram
// connected!" (components/IgConnectSteps doneView). With Reduce Motion on,
// everything fades and twinkles and nothing moves or changes size.

const ROSETTE = "M12 1.5L14.38 3.11L17.25 2.91L18.51 5.49L21.09 6.75L20.89 9.62L22.5 12L20.89 14.38L21.09 17.25L18.51 18.51L17.25 21.09L14.38 20.89L12 22.5L9.62 20.89L6.75 21.09L5.49 18.51L2.91 17.25L3.11 14.38L1.5 12L3.11 9.62L2.91 6.75L5.49 5.49L6.75 2.91L9.62 3.11Z";
const TICK = "M8 11.9l2.7 2.7L16 9.3";
const STAR = "M0-7C1-2 2-1 7 0 2 1 1 2 0 7-1 2-2 1-7 0-2-1-1-2 0-7Z";
const HEART = "M0 4.8C-5.6.9-6.4-1.9-5.2-3.8c1.3-1.9 3.9-1.6 5.2.4 1.3-2 3.9-2.3 5.2-.4C11.6-1.9 5.6.9 0 4.8Z";
const GOLD = "#ffd34d";

const Spark: React.FC<{ x: number; y: number; delay: number; children: React.ReactNode }> = ({ x, y, delay, children }) => (
  <g transform={`translate(${x} ${y})`}>
    <g className="vc-spark" style={{ "--vc-d": `${delay}ms` } as React.CSSProperties}>
      {children}
    </g>
  </g>
);

export default function VerifiedCelebration() {
  const id = useId().replace(/:/g, "");
  return (
    <svg className="vc" viewBox="0 0 280 170" aria-hidden="true">
      <defs>
        <radialGradient id={`vcGlow${id}`}>
          <stop offset="0" stopColor="#24c961" stopOpacity=".5" />
          <stop offset=".55" stopColor="#24c961" stopOpacity=".16" />
          <stop offset="1" stopColor="#24c961" stopOpacity="0" />
        </radialGradient>
        <linearGradient id={`vcShine${id}`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#fff" stopOpacity=".22" />
          <stop offset=".55" stopColor="#fff" stopOpacity="0" />
        </linearGradient>
        <linearGradient id={`vcBand${id}`} x1="0" x2="1">
          <stop offset="0" stopColor="#ffc72e" />
          <stop offset=".5" stopColor="#ffe98a" />
          <stop offset="1" stopColor="#ffc72e" />
        </linearGradient>
        <linearGradient id={`vcTail${id}`} x1="0" x2="1">
          <stop offset="0" stopColor="#ffbf3a" />
          <stop offset=".5" stopColor="#ffd76a" />
          <stop offset="1" stopColor="#ffbf3a" />
        </linearGradient>
      </defs>
      <g className="vc-glow">
        <circle cx="140" cy="84" r="112" fill={`url(#vcGlow${id})`} />
      </g>
      <g className="vc-ros">
        <svg x="64" y="6" width="152" height="152" viewBox="-1 -1 26 26" overflow="visible">
          <path transform="translate(0 1.1)" fill="#0d7a39" d={ROSETTE} />
          <path fill="#24c961" stroke="#24c961" strokeWidth="1.8" strokeLinejoin="round" d={ROSETTE} />
          <path fill={`url(#vcShine${id})`} d={ROSETTE} />
          <path className="vc-tick" pathLength={1} fill="none" stroke="#0a0d14" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" d={TICK} />
        </svg>
      </g>
      <g className="vc-tails">
        <path d="M62 106 C46 104 30 104 14 106 L28 123 L8 141 C26 137 46 136 64 139 Z" fill={`url(#vcTail${id})`} />
        <path d="M218 106 C234 104 250 104 266 106 L252 123 L272 141 C254 137 234 136 216 139 Z" fill={`url(#vcTail${id})`} />
      </g>
      <g className="vc-fold">
        <path d="M52 126 L66 148 L72 128 Z" fill="#c9791c" />
        <path d="M228 126 L214 148 L208 128 Z" fill="#c9791c" />
      </g>
      <g className="vc-band">
        <path d="M50 98 C92 124 188 124 230 98 L232 100 L230 124 C188 150 92 150 50 124 L48 100 Z" fill={`url(#vcBand${id})`} />
        <path d="M50 98 C92 124 188 124 230 98" fill="none" stroke="#fff" strokeOpacity=".35" strokeWidth="1.2" />
      </g>
      <Spark x={44} y={44} delay={780}><path transform="scale(1.5)" d={STAR} fill={GOLD} /></Spark>
      <Spark x={62} y={74} delay={900}><path transform="scale(.85)" d={STAR} fill={GOLD} /></Spark>
      <Spark x={232} y={74} delay={840}><path d={STAR} fill={GOLD} /></Spark>
      <Spark x={258} y={42} delay={960}><path transform="scale(.85)" d={STAR} fill={GOLD} /></Spark>
      <Spark x={28} y={68} delay={1000}><circle r="4.5" fill="#c9b6ff" /></Spark>
      <Spark x={228} y={34} delay={880}><path transform="scale(1.4)" d={HEART} fill="#ff5a7a" /></Spark>
    </svg>
  );
}
